import type { ReactNode } from 'react';
import { Card, EyebrowLabel } from '@thegpvc/ui';

type EyebrowColor = 'muted' | 'orange' | 'cream' | 'navy';

interface SectionCardProps {
  /** Eyebrow label text (tiny tracked uppercase section header). */
  title: ReactNode;
  /** Eyebrow color treatment. Defaults to orange (the standard section header). */
  eyebrowColor?: EyebrowColor;
  /** Show the leading dot on the eyebrow. Defaults to true. */
  dot?: boolean;
  /** Optional right-slot content in the header row (legend, count, copy button…). */
  action?: ReactNode;
  /** Card body class. Defaults to `space-y-3`; ignored when `flush`. */
  bodyClassName?: string;
  /**
   * Flush mode: render a padded eyebrow header, then the children edge-to-edge
   * (no body padding) — for full-bleed content like tables and heatmap grids
   * that supply their own padding.
   */
  flush?: boolean;
  /** Extra classes on the Card. */
  className?: string;
  children: ReactNode;
}

/**
 * SectionCard — the standard content card used across the dashboard and session
 * views: a `radius="card"` Card whose header is an {@link EyebrowLabel} (with an
 * optional right-slot action) over the body. Encapsulates the repeated
 * `<Card radius="card"><Card.Body><EyebrowLabel dot color="orange">…` pattern so
 * every section reads consistently.
 */
export function SectionCard({
  title,
  eyebrowColor = 'orange',
  dot = true,
  action,
  bodyClassName = 'space-y-3',
  flush = false,
  className,
  children,
}: SectionCardProps) {
  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <EyebrowLabel dot={dot} color={eyebrowColor}>{title}</EyebrowLabel>
      {action}
    </div>
  );

  if (flush) {
    // Header lives in a Card.Body so its padding matches non-flush section
    // cards (eyebrows align across the page); children render edge-to-edge.
    return (
      <Card radius="card" className={className}>
        <Card.Body className="pb-3">{header}</Card.Body>
        {children}
      </Card>
    );
  }

  return (
    <Card radius="card" className={className}>
      <Card.Body className={bodyClassName}>
        {header}
        {children}
      </Card.Body>
    </Card>
  );
}
